import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Calendar, ClipboardCheck, Send, FileText, Euro, FilePenLine,
  User as UserIcon, MapPin, ArrowRight, Filter,
} from "lucide-react";
import api, { getUser } from "@/lib/api";

/**
 * Painel do Consultor de Cuidados — RTs agendadas + RTs realizadas com
 * pendências. Consultor vê só as suas; Admin / Direcção / DGO / TSC vê
 * todas com filtro por Consultor. Ordenação por defeito: mais antigas
 * no topo (dias de pendência descendente).
 */
const REASON_ICON = {
  proposta_por_gerar:  FileText,
  proposta_por_enviar: FilePenLine,
  aguarda_decisao:     Send,
  deposito_pendente:   Euro,
  contrato_pendente:   ClipboardCheck,
};
const REASON_TONE = {
  proposta_por_gerar:  "bg-amber-500/15 text-amber-500 border-amber-500/30",
  proposta_por_enviar: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  aguarda_decisao:     "bg-violet-500/15 text-violet-500 border-violet-500/30",
  deposito_pendente:   "bg-rose-500/15 text-rose-500 border-rose-500/30",
  contrato_pendente:   "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};
const REASON_ORDER = [
  "contrato_pendente", "deposito_pendente", "aguarda_decisao",
  "proposta_por_enviar", "proposta_por_gerar",
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
}

function ReasonPill({ reason, label }) {
  const Icon = REASON_ICON[reason] || FileText;
  const tone = REASON_TONE[reason] || "bg-muted text-foreground border-border";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border ${tone}`}
      data-testid={`reason-pill-${reason}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function CompletedRow({ lead }) {
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors group"
      data-testid={`completed-row-${lead.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground truncate">
          {lead.name}
          {lead.office && (
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium bg-muted/60 px-2 py-0.5 rounded-full">
              {lead.office}
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
          {lead.phone && <span>{lead.phone}</span>}
          {lead.district && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {lead.district}
            </span>
          )}
          {lead.assigned_consultor_name && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="w-3 h-3" /> {lead.assigned_consultor_name}
            </span>
          )}
        </div>
      </div>
      <ReasonPill reason={lead.pending_reason} label={lead.pending_reason_label} />
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">RT realizada</div>
        <div className="text-[13px] text-foreground font-medium">{fmtDate(lead.rt_completed_at)}</div>
        <div className="text-[10.5px] text-muted-foreground mt-0.5">há {lead.pending_days ?? "?"} dia(s)</div>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
    </Link>
  );
}

function ScheduledRow({ lead }) {
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors group"
      data-testid={`scheduled-row-${lead.id}`}
    >
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-foreground truncate">{lead.name}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
          {lead.phone && <span>{lead.phone}</span>}
          {lead.district && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {lead.district}
            </span>
          )}
          {lead.assigned_consultor_name && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="w-3 h-3" /> {lead.assigned_consultor_name}
            </span>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">RT agendada</div>
        <div className="text-[13px] text-foreground font-medium">{fmtDate(lead.scheduled_qt_at)}</div>
        <div className="text-[10.5px] text-muted-foreground mt-0.5">
          {lead.days_until_rt == null ? "" :
            lead.days_until_rt === 0 ? "hoje" :
            lead.days_until_rt < 0 ? `há ${Math.abs(lead.days_until_rt)} dia(s)` :
            `em ${lead.days_until_rt} dia(s)`}
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
    </Link>
  );
}

export default function RTsPendentes() {
  const me = getUser();
  const isAdminLike = me && ["admin", "direcao", "dgo"].includes(me.role);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filterConsultor, setFilterConsultor] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const url = `/rts-pendentes${filterConsultor ? `?consultor_id=${encodeURIComponent(filterConsultor)}` : ""}`;
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erro a carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterConsultor]);

  // Group completed by reason for the summary strip.
  const groupedByReason = useMemo(() => {
    if (!data?.completed_pending) return {};
    const g = {};
    for (const r of REASON_ORDER) g[r] = [];
    for (const lead of data.completed_pending) {
      if (!g[lead.pending_reason]) g[lead.pending_reason] = [];
      g[lead.pending_reason].push(lead);
    }
    return g;
  }, [data]);

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="rts-pendentes-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Consultor de Cuidados</div>
        <h1 className="font-display text-3xl lg:text-4xl tracking-tight mt-1">Reuniões Técnicas — pendentes</h1>
        <div className="text-sm text-muted-foreground mt-1">
          Todos os leads em que a RT ainda não fechou o ciclo (rascunhos, envios, aceitações, cauções e contratos).
        </div>
      </div>

      {/* Admin filter dropdown */}
      {isAdminLike && data?.consultors?.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Filter className="w-3.5 h-3.5" /> Filtrar por Consultor:
          </div>
          <select
            value={filterConsultor}
            onChange={(e) => setFilterConsultor(e.target.value)}
            className="bg-card border border-border rounded-full px-3 py-1.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
            data-testid="filter-consultor"
          >
            <option value="">Todos os Consultores</option>
            {data.consultors.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {filterConsultor && (
            <button
              onClick={() => setFilterConsultor("")}
              className="text-xs text-muted-foreground underline hover:text-foreground"
              data-testid="clear-filter"
            >
              limpar filtro
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> A carregar…
        </div>
      ) : (
        <>
          {/* Summary strip */}
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="bg-card border border-border rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> Agendadas
                </div>
                <div className="font-display text-3xl mt-1 text-foreground" data-testid="count-scheduled">{data.counts.scheduled}</div>
              </div>
              {REASON_ORDER.map((reason) => {
                const cnt = data.counts.by_reason[reason] || 0;
                const Icon = REASON_ICON[reason];
                return (
                  <div key={reason} className="bg-card border border-border rounded-2xl p-4">
                    <div className={`text-[10px] uppercase tracking-[0.15em] font-semibold flex items-center gap-1.5 ${cnt > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                      <Icon className="w-3 h-3" />
                      {reason.replace(/_/g, " ")}
                    </div>
                    <div className={`font-display text-3xl mt-1 ${cnt > 0 ? "text-foreground" : "text-muted-foreground"}`} data-testid={`count-${reason}`}>{cnt}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* RT AGENDADAS */}
          <section className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="section-scheduled">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                <h2 className="font-ui text-base">RTs agendadas</h2>
                <span className="text-xs text-muted-foreground">({data?.counts.scheduled || 0})</span>
              </div>
              <div className="text-[11px] text-muted-foreground">Ordenadas da mais próxima para a mais distante</div>
            </div>
            {(!data?.scheduled?.length) ? (
              <div className="text-sm text-muted-foreground text-center py-10">
                Nenhuma RT agendada no momento.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {data.scheduled.map((lead) => <ScheduledRow key={lead.id} lead={lead} />)}
              </div>
            )}
          </section>

          {/* RT REALIZADAS PENDENTES */}
          <section className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="section-completed-pending">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-primary" />
                <h2 className="font-ui text-base">RTs realizadas · pendentes</h2>
                <span className="text-xs text-muted-foreground">({data?.counts.completed_pending || 0})</span>
              </div>
              <div className="text-[11px] text-muted-foreground">Mais antigas no topo</div>
            </div>
            {(!data?.completed_pending?.length) ? (
              <div className="text-sm text-muted-foreground text-center py-10">
                Nada pendente. Todos os leads com RT realizada já fecharam o ciclo. 🎉
              </div>
            ) : (
              <div className="divide-y divide-border">
                {data.completed_pending.map((lead) => <CompletedRow key={lead.id} lead={lead} />)}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
